import Button from '@material-ui/core/Button';
import { ArrowBack, CheckRounded } from '@material-ui/icons';
import {
  TURNSTILE_TOKEN_FIELD,
  TurnstileWidget,
  useTurnstile,
} from '@orientaction/components/Turnstile';
import { publicIconPath } from '@orientaction/utils';
import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { forgotPassword } from '../../../../services/user.service';
import { emailMatch } from '../../../../utils/constants';
import RedesignImage from '../../shared/components/RedesignImage';
import { useAuthStyles } from '../Auth.styles';
import HelpModal from '../components/HelpModal';
import locale from './locale.json';

const ForgotPasswordForm: FC = () => {
  const cls = useAuthStyles();
  const history = useHistory();
  const turnstile = useTurnstile();

  const [confirmed, setConfirmed] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);

  const { control, handleSubmit } = useForm<{ email: string }>({ mode: 'onChange' });

  const onSubmit = async (data: { email: string }) => {
    const turnstileToken = turnstile.getTokenOrError();
    if (!turnstileToken) {
      return;
    }

    setRegisterError('');
    setIsLoading(true);
    try {
      const response = await forgotPassword({
        ...data,
        [TURNSTILE_TOKEN_FIELD]: turnstileToken,
      });
      if (response?.data?.ok === true) {
        setConfirmed(true);
      } else {
        turnstile.reset();
        setRegisterError('Une erreur est survenue');
      }
    } catch {
      turnstile.reset();
      setRegisterError('Une erreur est survenue');
    } finally {
      setIsLoading(false);
    }
  };

  if (confirmed) {
    return (
      <>
        <div className={cls.statusContainer}>
          <div className={cls.checkCircle}>
            <CheckRounded className={cls.checkIcon} />
          </div>

          <h2 className={cls.statusTitle} data-cy="confirmation-account">
            {locale.confirmationTitle}
          </h2>

          <p className={cls.statusDescription}>{locale.confirmationDescription}</p>

          <button type="button" className={cls.backButton} onClick={() => history.push('/')}>
            <ArrowBack className={cls.backIcon} />
            <span>{locale.backText}</span>
          </button>
        </div>

        <button type="button" className={cls.outlineButton} onClick={() => setHelpOpen(true)}>
          <RedesignImage src={`${publicIconPath}/Info.svg`} alt="" width={16} height={16} />
          <span className={cls.outlineButtonTextPrimary}>Besoin d&apos;aide ?</span>
        </button>

        <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      </>
    );
  }

  return (
    <>
      <div className={cls.headingGroup}>
        <h1 className={cls.title}>{locale.title}</h1>
        <p className={cls.subtitle}>{locale.subtitle}</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className={cls.form}>
        <div className={cls.fieldsGroup}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            rules={{
              required: 'Champ obligatoire',
              pattern: {
                value: emailMatch,
                message: 'Veuillez saisir une adresse e-mail valide',
              },
            }}
            render={({ field, fieldState }) => (
              <label htmlFor="forgot-password-email" className={cls.field}>
                <span className={cls.label}>{locale.email}</span>
                <div className={cls.inputWrapper}>
                  <input
                    {...field}
                    id="forgot-password-email"
                    type="text"
                    placeholder={locale.emailPlaceholder}
                    className={`${cls.input} ${cls.inputWithIcon}`}
                  />
                  <RedesignImage
                    src={`${publicIconPath}/Email.svg`}
                    alt=""
                    className={cls.inputIcon}
                    style={{ cursor: 'default' }}
                  />
                </div>
                {fieldState.error?.message && (
                  <span className={cls.errorText}>{fieldState.error.message}</span>
                )}
              </label>
            )}
          />
        </div>

        {registerError && <p className={cls.errorTextMd}>{registerError}</p>}

        <TurnstileWidget {...turnstile.widgetProps} error={turnstile.error} />

        <Button type="submit" className={cls.primaryButton} disabled={isLoading}>
          {isLoading ? `${locale.sendText}...` : locale.sendText}
        </Button>
      </form>

      <button type="button" className={cls.backButton} onClick={() => history.push('/')}>
        <ArrowBack className={cls.backIcon} />
        <span>{locale.backText}</span>
      </button>

      <button type="button" className={cls.outlineButton} onClick={() => setHelpOpen(true)}>
        <RedesignImage src={`${publicIconPath}/Info.svg`} alt="" width={16} height={16} />
        <span className={cls.outlineButtonTextPrimary}>Besoin d&apos;aide ?</span>
      </button>

      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default ForgotPasswordForm;
